import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useOutletContext } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Reply as ReplyIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import ReplyModal from '../ReplyModal/ReplyModal';

import { setFeedbackStatus } from '../../../http/feedbackAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import { TABLE_THEME, USER_ROLES } from '../../../util/constants/AppConstants';

import './MessagesTable.scss';

const MessagesTable = ({ data, columns, loading }) => {
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const [rowSelection, setRowSelection] = useState({});
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [activeReplyMessage, setActiveReplyMessage] = useState(null);

  const { userInfo, handleGetFeedbackData, setIsModalOpen, setActiveMessage, setDeleteRequest, setDeleteId } =
    useOutletContext();

  const { pathname } = useLocation();

  const isMessages = pathname.startsWith('/emails');

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councilRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councilRoleValue;

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  const handleReplyMessage = (e, id) => {
    e.stopPropagation();

    const message = data.find((elem) => elem.id === id);

    if (!message.email) {
      dispatch(
        setNotify({
          status: true,
          type: '',
          title: 'Attention',
          message: 'Cannot send reply: constituent has no email',
        }),
      );
    } else {
      setIsReplyModalOpen(true);
      setActiveReplyMessage(message);
    }
  };

  const handleEditMessage = (e, id) => {
    e.stopPropagation();

    setIsModalOpen(true);
    const message = data.find((elem) => elem.id === id);
    setActiveMessage(message);
  };

  const handleDeleteMessage = (e, id) => {
    e.stopPropagation();

    setDeleteRequest(true);
    setDeleteId(id);
  };

  const theme = createTheme({
    ...TABLE_THEME,
    zIndex: {
      modal: 10000,
      popover: 10000,
      tooltip: 10001,
    },
  });

  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);
  const columnOrder = useMemo(
    () => [
      'mrt-row-select',
      'case_id',
      'importance',
      'status',
      `${isCouncillor ? 'ward' : 'poll'}_label`,
      'type',
      'name',
      'description',
      'source',
      'staffer_fullname',
      'date_utc',
    ],
    [isCouncillor],
  );

  const table = useMaterialReactTable({
    columns: memoColumns,
    data: memoData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableRowSelection: userInfo && isMessages,
    getIsRowSelectable: (row) => !row.original.readonly,
    muiSelectCheckboxProps: ({ row }) => ({
      disabled: row.original.readonly,
      sx: row.original.readonly
        ? {
            opacity: 0.4,
            cursor: 'not-allowed',
            '&.Mui-disabled': {
              color: '#B0B0B0',
            },
          }
        : {},
      title: row.original.readonly ? 'This email is read-only' : '',
    }),
    getRowId: (originalRow) => originalRow.id,
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;

        const filtered = Object.fromEntries(
          Object.entries(next).filter(([id]) => {
            const row = memoData.find((r) => String(r.id) === String(id));
            return row && !row.readonly;
          }),
        );

        return filtered;
      });
    },
    state: { rowSelection, isLoading: loading, columnOrder },
    onColumnOrderChange: () => {},
    positionToolbarAlertBanner: 'none',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      showFirstButton: true,
      showLastButton: true,
    },
    paginationDisplayMode: 'pages',
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      showColumnFilters: true,
    },
    muiTableBodyCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '14px',
        fontWeight: 'normal',
        border: 'none',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '16px',
        fontWeight: 500,
        border: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        borderTop: '1px solid #D3D3D3',
        borderBottom: '1px solid #D3D3D3',
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const { importance } = row.original;

      let backgroundColor = '#FFFFFF';
      let hoverColor = '#F7F7F7';

      if (importance === 3) {
        backgroundColor = '#FFE5E5';
        hoverColor = '#FFCCCC';
      } else if (importance === 2) {
        backgroundColor = '#FFF8E1';
        hoverColor = '#FFECB3';
      }

      return {
        onClick: () => {
          window.open(`/email/${row.original.id}`, '_blank');
        },
        sx: {
          cursor: 'pointer',
          borderBottom: '1px solid #D3D3D3',
          backgroundColor,
          '&:hover': {
            backgroundColor: hoverColor,
          },
          '&[data-selected=true]': {
            backgroundColor,
          },
          '&[data-selected=true]:hover': {
            backgroundColor: hoverColor,
          },
        },
      };
    },
    enableRowActions: isMessages,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => {
      if (row.original.readonly) {
        return null;
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
          <Tooltip title="Reply">
            <IconButton
              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
              onClick={(e) => handleReplyMessage(e, row.original.id)}
            >
              <ReplyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
              onClick={(e) => {
                handleEditMessage(e, row.original.id);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
              onClick={(e) => {
                handleDeleteMessage(e, row.original.id);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 140,
        grow: false,
        muiTableHeadCellProps: {
          sx: {
            fontSize: '16px',
            fontWeight: 500,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
      },
    },
  });

  const handleCreateNew = () => {
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async (status) => {
    if (Object.keys(rowSelection).length > 0) {
      const res = await setFeedbackStatus(config, userInfo.id, Object.keys(rowSelection), status);

      if (res.status === 200) {
        dispatch(
          setNotify({
            status: true,
            type: 'success',
            title: 'Success',
            message: res.data.message,
          }),
        );

        handleGetFeedbackData(userInfo.id, userInfo.role);
        setRowSelection({});
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: res.data.message,
          }),
        );
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="table">
        {isMessages && (
          <div className="table__btns">
            <div className="table__btn create-btn" onClick={handleCreateNew}>
              Create New +
            </div>
            <span className="table__counter">Selected ({Object.keys(rowSelection).length})</span>
            {(isMpp || isCouncillor) && (
              <div
                className={`table__btn create-btn secondary ${Object.keys(rowSelection).length === 0 ? 'disabled' : ''}`}
                onClick={() => handleUpdateStatus('Read')}
              >
                Mark As Read
              </div>
            )}
            <div
              className={`table__btn create-btn ${Object.keys(rowSelection).length === 0 ? 'disabled' : ''}`}
              onClick={() => handleUpdateStatus('Handled')}
            >
              Mark As Handled
            </div>
          </div>
        )}
        <MaterialReactTable table={table} />
      </div>
      {isReplyModalOpen && (
        <ReplyModal
          isOpen={isReplyModalOpen}
          setIsOpen={setIsReplyModalOpen}
          caseId={activeReplyMessage.case_id}
          recipientEmail={activeReplyMessage.email.split(';')[0]}
          handleGetFeedbackData={() => {}}
        />
      )}
    </ThemeProvider>
  );
};

export default MessagesTable;
